<template>
  <vx-tooltip :text="disabledHint" :enabled="disabled" position="bottom">
    <div class="w-full button rounded-lg p-2"
         :class="disabled ? 'cursor-not-allowed bg-white border-solid border border-soft' : 'bg-primary-light cursor-auto'"
         :key="addButtonKey">

      <div v-if="text && !disabled" class="text-center my-4 text-primary">
        {{ text }}
      </div>

      <div class="test-add-widget-options inline-flex w-full justify-around items-center">
        <base-button :variant="disabled ? 'transparent-disabled' : 'transparent-primary'"
                     :disabled="disabled"
                     class="w-full test-add-message"
                     @click="addBubble">
          <div class="flex items-center justify-center">
            <add-bubble-icon/>
            <span class="ml-2 font-medium">Message</span>
          </div>
        </base-button>

        <vx-tooltip text="Use Block" :enabled="!disabled" position="bottom" class="border-solid border-0 border-r border-l border-gray-a10">
          <base-button v-if="!isTemplateEditor"
                       :variant="isBlockBeingAdded ? 'transparent-disabled' : 'transparent-primary'"
                       class="flex test-add-template px-8 justify-center"
                       :disabled="isBlockBeingAdded"
                       @click="addTemplate"
          >
            <blocks-icon :class="{ 'text-gray': disabled }"/>
          </base-button>
        </vx-tooltip>

        <add-bubble-menu class="vs-button test-add-menu" :disabled="disabled">

          <vs-dropdown-item v-if="allowCondition" class="text-base pt-2"
                            @click="addCondition"
                            @mouseover="conditionIsHovered = true"
                            @mouseleave="conditionIsHovered = false">
            <div class="flex flew-row items-center pl-px">
              <condition-icon class="h-4 w-4"
                              :class="{ 'text-primary-focus': conditionIsHovered }"/>
              <span class="ml-3">Add Condition</span>
            </div>
          </vs-dropdown-item>

          <vs-dropdown-item class="text-base py-2"
                            :disabled="isWaitForInputDisabled"
                            @click="addWaitForUserInput"
                            @mouseover="waitForInputIsHovered = true"
                            @mouseleave="waitForInputIsHovered = false">
            <vx-tooltip class="flex flew-row items-center"
                        text="There must be a message preceding the Wait for customer input"
                        position="bottom"
                        :enabled="isWaitForInputDisabled"
            >
              <wait-for-input-icon class="h-5 w-5"
                                   :class="{ 'text-primary-focus': waitForInputIsHovered && !isWaitForInputDisabled }"/>
              <span class="ml-2">Wait for customer input</span>
            </vx-tooltip>
          </vs-dropdown-item>

          <vs-dropdown-item class="text-base pb-2"
                            @click="addRemoteBubble"
                            @mouseover="apiIsHovered = true"
                            @mouseleave="apiIsHovered = false">
            <div class="flex flew-row items-center">
              <api-cloud-icon class="h-5 w-5"
                              :class="{ 'text-primary-focus': apiIsHovered }"/>
              <span class="ml-2 pl-px">Make API call</span>
            </div>
          </vs-dropdown-item>

          <vs-dropdown-item class="text-base pb-2"
                            @click="addChatGPTBubble"
                            @mouseover="chatGPTIsHovered = true"
                            @mouseleave="chatGPTIsHovered = false">
            <div class="flex flew-row items-center">
              <GPT-icon class="h-5 w-5"
                              :class="{ 'text-primary-focus': chatGPTIsHovered }"/>
              <span class="ml-2 pl-px">AI answer</span>
            </div>
          </vs-dropdown-item>

          <vs-dropdown-item class="text-base pb-2"
                            @click="addProductRecommendationBubble"
                            @mouseover="productRecommendationIsHovered = true"
                            @mouseleave="productRecommendationIsHovered = false">
            <div class="flex flew-row items-center">
              <feather-icon icon="ShoppingBagIcon"
                            svg-classes="h-5 w-5"
                            :class="{ 'text-primary-focus': productRecommendationIsHovered && productFeedConnected, 'text-gray': !productFeedConnected }"/>
              <!-- PRO-740: without a product feed the answer cannot be saved — the entry leads to creating one -->
              <span v-if="productFeedConnected" class="ml-2 pl-px whitespace-nowrap">Product recommendations</span>
              <span v-else class="ml-2 pl-px flex flex-col">
                <span class="whitespace-nowrap text-gray">Product recommendations</span>
                <span class="text-xs text-gray">Connect a product feed first →</span>
              </span>
            </div>
          </vs-dropdown-item>

        </add-bubble-menu>
      </div>

    </div>
  </vx-tooltip>
</template>

<script>
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import previewModule from '@/store/modules/preview.module'
import templateSelectorModule from '@/store/modules/template-selector.module'
import templateModule from '@/store/modules/template.module'
import ApiCloudIcon from '@/assets/images/icons/ApiCloudIcon'
import GPTIcon from '@/assets/images/icons/GPTIcon.vue'
import ConditionIcon from '@/assets/images/icons/ConditionIcon'
import WaitForInputIcon from '@/assets/images/icons/WaitForInputIcon'
import AddBubbleMenu from './AddBubbleMenu'
import answerController from '@/controllers/answer.controller'
import templateController from '@/controllers/template.controller'
import AddBubbleIcon from '@/assets/images/icons/AddBubbleIcon'
import BlocksIcon from '@/assets/images/icons/BlocksIcon'
import { NODE_TYPES } from '@/utils/state-tree.generator'
import { VALIDATION_PARAMS } from '@/utils/validation.constants'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import { CONVERSATION_TYPES } from '@/store/constants'
import { ROUTE_NAMES } from '@/router'
import { ITEM_IDS } from '@/utils/editor.constants'
import { hasProductFeed } from '@/services/amio/product-preview.service'

export default {
  name: 'AddBubbleWidget',

  components: {
    BlocksIcon, ApiCloudIcon, ConditionIcon, WaitForInputIcon, AddBubbleMenu, AddBubbleIcon, GPTIcon
  },

  props: {
    allowCondition: { type: Boolean, required: true },
    disabled: { type: Boolean, default: false },
    disabledHint: { type: String, default: null },
    node: { type: Object, required: true },
    isCondition: { type: Boolean, default: false },
    answerEndType: { type: String, default: null }
  },

  data() {
    return {
      apiIsHovered: false,
      conditionIsHovered: false,
      waitForInputIsHovered: false,
      chatGPTIsHovered: false,
      productRecommendationIsHovered: false,
      // optimistic: the check below only ever takes the option away
      productFeedConnected: true,
      addButtonKey: 1
    }
  },

  mounted() {
    hasProductFeed().then(connected => { this.productFeedConnected = connected })
  },

  computed: {
    bubbleSelected: chatBubbleModule.bubbleSelected,
    focus: chatBubbleModule.sectionFocus,
    template: templateModule.template,
    conversationType: conversationModule.getType,
    error: previewModule.error,

    isTemplateEditor() {
      return templateModule.isTemplateEditor()
    },

    previewController() {
      return this.isTemplateEditor ? templateController : answerController
    },

    stateId() {
      return this.node.stateId
    },

    isWaitForInputDisabled() {
      return this.disabled || this.node.type !== NODE_TYPES.OUTPUT
    },

    isBlockBeingAdded() {
      return this.disabled || busyButtonsModule.isButtonBusy(BUSY_BUTTONS.BLOCK_ADD)
    },

    text() {
      if (this.node.type !== NODE_TYPES.EMPTY || this.node.parent) return ''

      switch (this.conversationType) {
        case CONVERSATION_TYPES.ANSWERS:
          return 'Answer the request'
        case CONVERSATION_TYPES.EVENTS:
          return 'React to event'
        case CONVERSATION_TYPES.TEMPLATES:
          return 'Begin block'
        default:
          return ''
      }
    }
  },

  methods: {
    addBubble() {
      if (this.disabled) return

      const isAddingToEmptyPostback = previewModule.isAddingToEmptyPostback(this.node)
      if(this.isTemplateEditor) {
        templateController.addBubble(this.node, this.isCondition, this.template?.id, this.answerEndType)
      } else {
        answerController.addBubble(this.node, this.isCondition)
      }

      this.resetAddButton()
      this.resetError(isAddingToEmptyPostback)
    },

    addTemplate() {
      if (this.disabled) return

      templateSelectorModule.showSelector(this.stateId, this.isCondition)
    },

    addWaitForUserInput() {
      if (this.isWaitForInputDisabled) return

      this.addWaitForUserInputWithValidationAndContext()
    },

    addRemoteBubble() {
      const isAddingToEmptyPostback = previewModule.isAddingToEmptyPostback(this.node)
      this.previewController.addRemoteBubble(this.node, this.isCondition, this.template?.id, this.answerEndType)
      this.resetError(isAddingToEmptyPostback)
    },

    addChatGPTBubble() {
      const isAddingToEmptyPostback = previewModule.isAddingToEmptyPostback(this.node)
      this.previewController.addChatGPTBubble(this.node, this.isCondition, this.template?.id, this.answerEndType)
      this.resetError(isAddingToEmptyPostback)
    },

    async addProductRecommendationBubble() {
      // the menu's flag is optimistic until the check returns (and may be a minute old) — ask on the click itself
      this.productFeedConnected = await hasProductFeed()
      if (!this.productFeedConnected) {
        this.$router.push({ name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: ITEM_IDS.NEW } })
        return
      }

      const isAddingToEmptyPostback = previewModule.isAddingToEmptyPostback(this.node)
      this.previewController.addChatGPTBubble(this.node, this.isCondition, this.template?.id, this.answerEndType, true)
      this.resetError(isAddingToEmptyPostback)
    },

    addCondition() {
      const isAddingToEmptyPostback = previewModule.isAddingToEmptyPostback(this.node)
      this.previewController.addConditionSection(this.node, this.template?.id)
      this.resetError(isAddingToEmptyPostback)
    },

    resetAddButton() {
      this.addButtonKey = Math.random()
    },

    resetError(isAddingToEmptyPostback) {
      if (isAddingToEmptyPostback) this.error = null
    },

    addWaitForUserInputWithValidationAndContext() {
      const { transitionStateId, nextStateId } = this.previewController.addWaitForUserInput(this.node, this.isCondition, this.template?.id)
      const validationActionId = this.previewController.addValidationAction(transitionStateId, VALIDATION_PARAMS.email)
      const contextStateId = this.previewController.generateId(this.stateId)
      this.previewController.addSaveContextState(this.stateId, nextStateId, contextStateId, VALIDATION_PARAMS.email.pattern, this.template?.id)
      chatBubbleModule.selectWaitForInput(transitionStateId, nextStateId, validationActionId, contextStateId)
    }
  }
}
</script>

<style lang="scss">
.test-add-menu-items {
  .vs-dropdown--item .vs-dropdown--item-link.disabled {
    pointer-events: auto !important;
    cursor: not-allowed !important;
  }
}
</style>
